import React, { useEffect, useState, lazy, Suspense, memo } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useMobile } from '@/hooks/use-mobile';
import { RefreshCw } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { SpeedInsights } from '@vercel/speed-insights/react';
import { getCurrentUserId } from '@/lib/auth-helper';
import { dualAuthService } from '@/lib/dual-auth-service';
// import PageTransition from "./components/ui/page-transition";

// Helper function to handle lazy load errors
const lazyLoadWithRetry = (importFunc: () => Promise<any>, retries = 3) => {
  return lazy(() => 
    importFunc().catch((error) => {
      console.error('Dynamic import failed:', error);
      
      // If it's a chunk load error and we have retries left, try again after a delay
      if ((error.message?.includes('Loading chunk') || 
           error.message?.includes('Failed to fetch dynamically imported module') ||
           error.name === 'ChunkLoadError') && retries > 0) {
        console.log(`Retrying import in 1 second... (${retries} retries left)`);
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(lazyLoadWithRetry(importFunc, retries - 1));
          }, 1000);
        });
      }
      
      // If no retries left or different error, reload the page
      console.log('No retries left or unrecoverable error, reloading page...');
      window.location.reload();
      
      // Return a fallback component while reloading
      return Promise.resolve({
        default: () => (
          <div className="min-h-screen bg-gradient-background text-white flex items-center justify-center">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
              <p>Loading latest version...</p>
            </div>
          </div>
        )
      });
    })
  );
};

// Lazy load pages with retry logic
const Login = lazyLoadWithRetry(() => import('@/pages/Login'));
const Signup = lazyLoadWithRetry(() => import('@/pages/Signup'));
const Onboarding = lazyLoadWithRetry(() => import('@/pages/Onboarding'));
const Chat = lazyLoadWithRetry(() => import('@/pages/Chat'));
const Landing = lazyLoadWithRetry(() => import('@/pages/Landing'));
const Help = lazyLoadWithRetry(() => import('@/pages/Help'));
const History = lazyLoadWithRetry(() => import('@/pages/History'));
const Settings = lazyLoadWithRetry(() => import('@/pages/Settings'));
const Privacy = lazyLoadWithRetry(() => import('@/pages/Privacy'));
const Terms = lazyLoadWithRetry(() => import('@/pages/Terms'));
const PrivacySecurity = lazyLoadWithRetry(() => import('@/pages/PrivacySecurity'));
const NotFound = lazyLoadWithRetry(() => import('@/pages/NotFound'));
const Voice = lazyLoadWithRetry(() => import('@/pages/Voice'));

// Coach-specific pages with retry logic
const CoachDashboard = lazyLoadWithRetry(() => import('@/pages/coach/CoachDashboard'));
const ClientChatMonitoring = lazyLoadWithRetry(() => import('@/pages/coach/ClientChatMonitoring'));
const CoachDirectMessage = lazyLoadWithRetry(() => import('@/pages/coach/CoachDirectMessage'));
const CoachDirectMessageChat = lazyLoadWithRetry(() => import('@/pages/coach/CoachDirectMessageChat'));
const ClientConversationMonitoring = lazyLoadWithRetry(() => import('@/pages/coach/ClientConversationMonitoring'));

// Direct approach coach pages (alternative method)
const ClientListDirect = lazyLoadWithRetry(() => import('@/pages/coach/ClientListDirect'));
const ClientChatMonitoringDirect = lazyLoadWithRetry(() => import('@/pages/coach/ClientChatMonitoringDirect'));
const ClientListSimple = lazyLoadWithRetry(() => import('@/pages/coach/ClientListSimple'));

// Fixed approach coach pages (works without auth_user_id column)
const ClientListFixed = lazyLoadWithRetry(() => import('@/pages/coach/ClientListFixed'));
const ClientChatMonitoringFixed = lazyLoadWithRetry(() => import('@/pages/coach/ClientChatMonitoringFixed'));

// Optimized approach coach page (fast loading)
const ClientListOptimized = lazyLoadWithRetry(() => import('@/pages/coach/ClientListOptimized'));

// Client direct messages page
const ClientDirectMessages = lazyLoadWithRetry(() => import('@/pages/ClientDirectMessages'));

// Role-based routing components
const RoleBasedLanding = lazyLoadWithRetry(() => import('@/components/auth/RoleBasedLanding'));
const RoleBasedVoice = lazyLoadWithRetry(() => import('@/components/auth/RoleBasedVoice'));

// Development tools
const RoleManager = lazyLoadWithRetry(() => import('@/components/dev/RoleManager'));

// Loading fallback component
const LoadingFallback = memo(() => (
  <div className="min-h-screen bg-gradient-background flex items-center justify-center">
    <div className="text-center">
      <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
      <p className="mt-4 text-white opacity-80 text-sm sm:text-base">Loading...</p>
    </div>
  </div>
));
LoadingFallback.displayName = 'LoadingFallback';

// Route logger component
const RouteLogger = memo(() => {
  const location = useLocation();
  const [routeChanges, setRouteChanges] = useState<{path: string, time: number}[]>([]);
  const [showResetButton, setShowResetButton] = useState(false);
  
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log("Route changed:", location.pathname);
    }
    
    // Add this route change to our tracking array
    setRouteChanges(prev => {
      const now = Date.now();
      const newChanges = [...prev, {path: location.pathname, time: now}];
      
      // Only keep the last 10 route changes
      if (newChanges.length > 10) {
        return newChanges.slice(-10);
      }
      return newChanges;
    });
  }, [location]);
  
  // Check for potential navigation loops
  useEffect(() => {
    if (routeChanges.length < 5) return;
    
    // Check if we have 5+ route changes in the last 3 seconds
    const now = Date.now();
    const recentChanges = routeChanges.filter(change => now - change.time < 3000);
    
    if (recentChanges.length >= 5) {
      console.warn("Potential navigation loop detected!");
      setShowResetButton(true);
    }
  }, [routeChanges]);

  const handleReset = async () => {
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Reload the app
      window.location.href = '/';
    } catch (error) {
      console.error("Error resetting application:", error);
      // Force reload anyway
      window.location.reload();
    }
  };
  
  if (!showResetButton) return null;
  
  return (
    <div 
      className="fixed bottom-4 right-4 z-50 bg-red-500 text-white p-2 rounded-full cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
      onClick={handleReset}
      title="Reset App (Emergency)"
    >
      <RefreshCw className="w-6 h-6" />
    </div>
  );
});
RouteLogger.displayName = 'RouteLogger';

// Error boundary component
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: Error | null}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("React error boundary caught error:", error, errorInfo);
    
    // Check if it's a dynamic import failure
    if (error.message?.includes('Failed to fetch dynamically imported module') || 
        error.message?.includes('Loading chunk') ||
        error.message?.includes('Loading CSS chunk')) {
      console.log('Dynamic import failure detected, will auto-reload...');
      // Auto-reload after a short delay to allow user to see the error
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    }
  }

  render() {
    if (this.state.hasError) {
      const isDynamicImportError = this.state.error?.message?.includes('Failed to fetch dynamically imported module') || 
                                  this.state.error?.message?.includes('Loading chunk') ||
                                  this.state.error?.message?.includes('Loading CSS chunk');
      
      return (
        <div className="min-h-screen bg-gradient-background text-white flex flex-col items-center justify-center p-4">
          <h1 className="text-3xl mb-4">
            {isDynamicImportError ? 'App Update Available' : 'Something went wrong'}
          </h1>
          <p className="text-red-400 mb-6">
            {isDynamicImportError 
              ? 'A new version is available. The page will reload automatically...' 
              : (this.state.error?.message || "Unknown error")
            }
          </p>
          {isDynamicImportError && (
            <div className="flex items-center gap-2 mb-4 text-yellow-400">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Auto-reloading in 3 seconds...</span>
            </div>
          )}
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-2 bg-gradient-primary text-slate-800 rounded-lg transition-all duration-300 hover:scale-105 active:scale-95"
          >
            Reload Now
          </button>
          {!isDynamicImportError && (
            <button 
              onClick={() => {
                localStorage.clear();
                window.location.reload();
              }}
              className="px-6 py-2 bg-red-500 text-white rounded-lg mt-4 transition-all duration-300 hover:scale-105 active:scale-95"
            >
              Reset & Reload
            </button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// AppRoutes component to wrap routes
const AppRoutes = memo(() => {
  return (
    <Routes>
      <Route path="/" element={<Onboarding />} />
      <Route path="/landing" element={<RoleBasedLanding />} />
      <Route path="/chat-landing" element={<RoleBasedLanding />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/chat" element={<Chat />} />
      <Route path="/voice" element={<RoleBasedVoice />} />
      <Route path="/help" element={<Help />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/history" element={<History />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy-security" element={<PrivacySecurity />} />
      
      {/* Coach-specific routes */}
      <Route path="/coach/dashboard" element={<CoachDashboard />} />
      <Route path="/coach/clients" element={<ClientConversationMonitoring />} />
      <Route path="/coach/client-chat/:clientId" element={<ClientChatMonitoring />} />
      <Route path="/coach/direct-message" element={<CoachDirectMessage />} />
      <Route path="/coach/direct-message-chat/:clientId" element={<CoachDirectMessageChat />} />
      
      {/* Direct approach routes (alternative method) */}
      <Route path="/coach/clients-direct" element={<ClientListDirect />} />
      <Route path="/coach/clients-simple" element={<ClientListSimple />} />
      <Route path="/coach/client-chat-direct/:sessionId" element={<ClientChatMonitoringDirect />} />
      
      {/* Fixed approach routes (works without auth_user_id column) */}
      <Route path="/coach/clients-fixed" element={<ClientListFixed />} />
      <Route path="/coach/client-chat-fixed/:clientId" element={<ClientChatMonitoringFixed />} />
      
      {/* Optimized approach route (fast loading) */}
      <Route path="/coach/clients-optimized" element={<ClientListOptimized />} />
      
      {/* Client direct messages route */}
      <Route path="/client-direct-messages" element={<ClientDirectMessages />} />
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
});
AppRoutes.displayName = 'AppRoutes';

// Create a singleton QueryClient to avoid unnecessary re-renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const App = () => {
  const { isMobile, isIOS } = useMobile();

  useEffect(() => {
    // iOS specific body fixes for vh units and overscroll
    if (isIOS) {
      document.body.classList.add('ios-device');
    }
    
    // Add touch optimization class for mobile devices
    if (isMobile) {
      document.body.classList.add('mobile-device', 'mobile-scroll-fix');
    }
    
    // Global error handler for dynamic import failures
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      
      // Check if it's a dynamic import failure
      if (error?.message?.includes('Failed to fetch dynamically imported module') ||
          error?.message?.includes('Loading chunk') ||
          error?.message?.includes('Loading CSS chunk') ||
          error?.name === 'ChunkLoadError') {
        console.log('Dynamic import failure detected globally, reloading...', error);
        event.preventDefault(); // Prevent console error
        
        // Show a brief message before reloading
        const notification = document.createElement('div');
        notification.innerHTML = `
          <div style="
            position: fixed; 
            top: 20px; 
            right: 20px; 
            background: #10b981; 
            color: white; 
            padding: 16px 24px; 
            border-radius: 8px; 
            z-index: 9999;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
            font-family: system-ui, -apple-system, sans-serif;
            max-width: 300px;
          ">
            <strong>App Update Available</strong><br>
            <small>Reloading to get the latest version...</small>
          </div>
        `;
        document.body.appendChild(notification);
        
        // Reload after a brief delay
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    };
    
    // Add global error handler
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    // Add viewport height fix for mobile browsers
    function setVh() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.documentElement.style.setProperty('--real-vh', `${vh}px`);
    }
    
    // Initial call and add resize listener
    setVh();
    
    // Throttle resize events for better performance
    let resizeTimer: number;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        setVh();
      }, 100);
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    // Handle viewport changes for mobile browsers
    if ('visualViewport' in window && window.visualViewport) {
      const visualViewport = window.visualViewport;
      
      const handleViewportChange = () => {
        // Account for keyboard appearance/disappearance
        const vh = visualViewport.height * 0.01;
        document.documentElement.style.setProperty('--real-vh', `${vh}px`);
      };
      
      visualViewport.addEventListener('resize', handleViewportChange);
    }
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      clearTimeout(resizeTimer);
    };
  }, [isMobile, isIOS]);

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <ErrorBoundary>
            <Router>
              <RouteLogger />
              <Suspense fallback={<LoadingFallback />}>
                <AppRoutes />
              </Suspense>
              {/* Development tools */}
              {import.meta.env.DEV && (
                <Suspense fallback={null}>
                  <RoleManager />
                </Suspense>
              )}
            </Router>
          </ErrorBoundary>
        </TooltipProvider>
      </QueryClientProvider>
      <SpeedInsights />
    </>
  );
};

export default App;
